import React, { useCallback, useEffect, useRef, useState } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import debounce from 'lodash.debounce';

import { Autocomplete, AutocompleteResults } from '@origin-digital/ods-lab';
import {
  Box,
  Button,
  Column,
  Columns,
  Divider,
  FormHelperText,
  IconButton,
  MarkdownLite,
  Stack,
  Text,
} from '@origin-digital/ods-core';
import { IconCloseCircle, IconSearch } from '@origin-digital/ods-icons';
import type {
  AutocompleteInputChangeReason,
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';

import { AddressResult } from '../models';
import { call, createSafeLabel, getOptionLabel } from '../helpers';
import { AddressManualEntryDefault } from '../AddressManualEntry';
import type { AddressLookupProps } from './AddressLookup';
import { content } from './content';
import { isQuerySearchable } from './helpers';
import { matchSorter } from 'match-sorter';
import styled from 'styled-components';

export type UpliftedAddressLookupProps = AddressLookupProps & {
  ariaLabel?: string;
  hasResolvedAddress?: boolean;
  /** Overrides the CTA text in both address states; omitted preserves the existing labels. */
  ctaLabel?: string;
  /** Ignores CTA clicks while the consumer is not ready. */
  ctaBlocked?: boolean;
  ctaLoading?: boolean;
  onCtaClick?: () => void;
  onClear?: () => void;
};

const SEARCH_CTA_LABEL = 'Search';
const VIEW_PLANS_CTA_LABEL = 'View my plans';

/**
 * ODS Autocomplete overlaps its input by default. This composite control needs
 * results below it, with a divider and clipping around the rounded menu.
 */
const UpliftedAutocompleteResults = styled(AutocompleteResults)`
  margin-top: 0;
  border-top: 1px solid ${({ theme }) => theme.palette.grey[200]};
  overflow: hidden;
`;

/**
 * ODS has no grouped TextField + Button state ring, hero-sized TextField, or
 * input typography/truncation props. These rules let the full control own its
 * state while preserving the specified 64px input and single-line address.
 */
const UpliftedAutocompleteContainer = styled(Box)<{ $error: boolean }>`
  outline: ${({ $error, theme }) => ($error ? `2px solid ${theme.palette.error.main}` : 'none')};

  &:hover {
    outline: ${({ $error, theme }) =>
      $error ? `2px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.text.primary}`};
  }

  &:focus-within {
    outline: 2px solid
      ${({ $error, theme }) => ($error ? theme.palette.error.main : theme.palette.info.main)};
  }

  && .MuiOutlinedInput-notchedOutline {
    border-style: none;
  }

  && .MuiOutlinedInput-root {
    min-height: ${({ theme }) => theme.space.xxxlarge}px;
  }

  .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
    overflow: hidden;
    padding-left: ${({ theme }) => theme.space.large + theme.space.xsmall}px !important;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
      padding-left: ${({ theme }) => theme.space.xxlarge}px !important;
    }
  }
`;

/**
 * ods-lab Autocomplete overwrites TextField's start adornment, so ODS startIcon
 * is not rendered here. Keep the search icon overlaid until that API is forwarded.
 */
const UpliftedSearchIcon = styled(Box)`
  top: ${({ theme }) => theme.space.xsmall + theme.space.xxxlarge / 2}px;
  transform: translateY(-50%);

  ${({ theme }) => theme.breakpoints.up('sm')} {
    top: 50%;
  }
`;

/** ODS Column hardcodes full height internally, which over-expands the collapsed mobile layout. */
const UpliftedColumn = styled(Column)`
  > div {
    height: auto;
  }
`;

type AutocompletePopperProps = React.ComponentProps<typeof AutocompleteResults>;

export const UpliftedAddressLookup = ({
  id,
  ariaLabel,
  disabled,
  hasResolvedAddress = false,
  placeholder = content.placeholder,
  helperText = '',
  label = '',
  error,
  value,
  manualEntry,
  manualEntryProps,
  endIcon,
  forcePopupIcon,
  optionalStreetType,
  onBlur,
  onCtaClick,
  ctaLabel,
  ctaBlocked = false,
  ctaLoading = false,
  fetchAddresses,
  onSelect,
  onError,
  onClear,
  onQueryChange,
  AddressManualEntryComponent = AddressManualEntryDefault,
  ...props
}: UpliftedAddressLookupProps) => {
  const [options, setOptions] = useState<AddressResult[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [loading, setLoading] = useState(false);
  const pendingRequests = useRef(0);
  const hasPendingDebouncedRequest = useRef(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const enterAddressManuallyBtnRef = useRef<HTMLButtonElement>(null);
  const autocompleteShellRef = useRef<HTMLDivElement>(null);

  const isPostcode = /^\d{4}$/.test(String(value).trim());

  const isControlled = value !== undefined;
  const dataId = props['data-id'] || id;
  const shouldFetchAddresses = isControlled && (isQuerySearchable(value) || value.length > 3);

  const updateLoading = () => {
    setLoading(hasPendingDebouncedRequest.current || pendingRequests.current > 0);
  };
  const handleDelayedFetchOptions = useCallback(
    debounce(async (newInputValue: string) => {
      hasPendingDebouncedRequest.current = false;
      pendingRequests.current += 1;
      setLoading(true);

      try {
        const result = await fetchAddresses(newInputValue);

        if (result.length > 0) {
          setOptions(result);
        }
      } catch (error) {
        call(onError, error as Error);
      } finally {
        pendingRequests.current -= 1;
        updateLoading();
      }
    }, 400),
    []
  );

  const queueAddressFetch = (newInputValue: string) => {
    hasPendingDebouncedRequest.current = true;
    setLoading(true);
    handleDelayedFetchOptions(newInputValue);
  };

  const cancelPendingAddressFetch = () => {
    handleDelayedFetchOptions.cancel();
    hasPendingDebouncedRequest.current = false;
    updateLoading();
  };

  useEffect(() => {
    onManualEntryClose();

    // clear pre-loaded options when value is cleared
    if (value === undefined || value === null || value === '') {
      setOptions([]);
    }

    if (shouldFetchAddresses) {
      queueAddressFetch(value);
    } else {
      cancelPendingAddressFetch();
    }
  }, [value]);

  const filterOptions = (options: AddressResult[], params: FilterOptionsState<AddressResult>) => {
    return matchSorter<AddressResult>(options, params.inputValue, {
      keys: ['label'],
      threshold: matchSorter.rankings.NO_MATCH,
    });
  };

  const handleClear = () => {
    setInputValue('');
    call(onQueryChange, '');
    onClear?.();
  };

  const onChange = (_: React.SyntheticEvent, newValue: AddressResult | null) => {
    if (!newValue) {
      handleClear();
      return;
    }

    if (onSelect) {
      void onSelect(newValue);
    }

    setInputValue(getOptionLabel(newValue));
  };

  const onInputChange = async (
    _: React.SyntheticEvent<Element, Event>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'reset') {
      return;
    }

    call(onQueryChange, newInputValue);

    setInputValue(newInputValue);

    if (!isQuerySearchable(newInputValue)) {
      cancelPendingAddressFetch();
      return;
    }

    queueAddressFetch(newInputValue);
  };

  const onManualEntryOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualEntryOpen?.();
  };

  const onManualNMIOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualNMIOpen?.();
  };

  const onManualMIRNOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualMIRNOpen?.();
  };

  const onManualEntryClose = () => {
    setShowManualEntry(false);
    manualEntryProps?.onManualEntryClose?.();
  };

  const onManualLocIdOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualLocIdOpen?.();
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AddressResult) => {
    const { label } = option;

    if (!label) return null;

    const matches = match(label, inputValue);
    const parts = parse(label, matches);

    return (
      <li {...props}>
        <Text>
          <MarkdownLite>
            {parts.map(({ highlight, text }) => (highlight ? `**${text}**` : text)).join('')}
          </MarkdownLite>
        </Text>
      </li>
    );
  };

  const focusLockPopper = (
    event: React.SyntheticEvent & { relatedTarget?: EventTarget | null }
  ) => {
    const { relatedTarget } = event;

    //  Prevent autocomplete drop-down from closing
    if (
      relatedTarget &&
      (relatedTarget as HTMLElement).id === enterAddressManuallyBtnRef.current?.id
    ) {
      return;
    }

    setAutocompleteOpen(false);
  };

  // A11y ensures Manual Address entry receives focus
  const focusOnTab = (event: React.KeyboardEvent) => {
    const enterAddressBtn = enterAddressManuallyBtnRef.current;

    if (event.code === 'Tab' && autocompleteOpen && enterAddressBtn) {
      event.preventDefault();
      enterAddressBtn.focus();
    }
  };

  const renderPopper = (popperProps: AutocompletePopperProps) => {
    if (isQuerySearchable(inputValue) || isPostcode || shouldFetchAddresses) {
      // ODS sizes results to the input column; this search needs them to span the input and CTA.
      const autocompleteCard = autocompleteShellRef.current
        ?.firstElementChild as HTMLElement | null;

      return (
        <UpliftedAutocompleteResults
          {...popperProps}
          anchorEl={autocompleteCard || popperProps.anchorEl}
          style={{
            ...popperProps.style,
            width: autocompleteCard?.clientWidth || popperProps.style?.width,
          }}
        >
          {(inputValue?.length >= 1 || !manualEntry) && popperProps.children}
          {manualEntry && (
            <Box>
              <Divider />
              <Box paddingX="medium" paddingY="small">
                <Stack space="medium" alignX="left">
                  <Button
                    noTextPadding
                    id="autocomplete-manual-address-trigger"
                    data-id="autocomplete-manual-address-trigger"
                    size="small"
                    variant="text"
                    onClick={onManualEntryOpen}
                    muiProps={{
                      ref: enterAddressManuallyBtnRef,
                      onMouseDown: (event) => event.preventDefault(),
                    }}
                  >
                    Enter address manually
                  </Button>
                  {manualEntryProps?.onManualNMIOpen && (
                    <Button
                      noTextPadding
                      id="autocomplete-manual-nmi-trigger"
                      data-id="autocomplete-manual-nmi-trigger"
                      size="small"
                      variant="text"
                      onClick={onManualNMIOpen}
                      muiProps={{
                        ref: enterAddressManuallyBtnRef,
                        onMouseDown: (event) => event.preventDefault(),
                      }}
                    >
                      Enter NMI manually
                    </Button>
                  )}
                  {manualEntryProps?.onManualMIRNOpen && (
                    <Button
                      noTextPadding
                      id="autocomplete-manual-mirn-trigger"
                      data-id="autocomplete-manual-mirn-trigger"
                      size="small"
                      variant="text"
                      onClick={onManualMIRNOpen}
                      muiProps={{
                        ref: enterAddressManuallyBtnRef,
                        onMouseDown: (event) => event.preventDefault(),
                      }}
                    >
                      Enter MIRN manually
                    </Button>
                  )}
                  {manualEntryProps?.onManualLocIdOpen && (
                    <Button
                      noTextPadding
                      id="autocomplete-manual-loc-id-trigger"
                      data-id="autocomplete-manual-loc-id-trigger"
                      size="small"
                      variant="text"
                      onClick={onManualLocIdOpen}
                      muiProps={{
                        ref: enterAddressManuallyBtnRef,
                        onMouseDown: (event) => event.preventDefault(),
                      }}
                    >
                      Enter LOC ID manually
                    </Button>
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </UpliftedAutocompleteResults>
      );
    }

    return null;
  };

  const muiProps: Partial<MuiAutocompleteProps<AddressResult, false, boolean, false>> = {
    renderOption,
    loading,
    onChange,
    onInputChange,
    filterOptions,
    getOptionLabel,
    inputValue: isControlled ? value : inputValue,
    open: autocompleteOpen,
    PopperComponent: renderPopper,
    onOpen: () => setAutocompleteOpen(true),
    onClose: focusLockPopper,
    onKeyDown: focusOnTab,
    noOptionsText: isPostcode ? content.postcodeNoOptionsText : content.noOptionsText,
    blurOnSelect: 'touch' as const,
  };

  const manualProps = {
    ...manualEntryProps,
    id: `${id}-manual-entry`,
    'data-id': `${dataId}-manual-entry`,
  };

  const hasInputValue = Boolean(isControlled ? value : inputValue);
  const ctaHelperTextId = `${id}-cta-helper-text`;
  const renderClearAddressButton = () => (
    <IconButton
      aria-label="Clear address"
      data-id={`${dataId}-clear`}
      noIconPadding
      onClick={handleClear}
      variant="transparent"
      muiProps={{ onMouseDown: (event) => event.preventDefault() }}
    >
      <IconCloseCircle color="secondaryB" isDecorative size="xsmall" variant="line" />
    </IconButton>
  );
  const upliftedEndIcon = endIcon || (hasInputValue ? renderClearAddressButton() : undefined);

  const focusAddressInput = () => {
    autocompleteShellRef.current?.querySelector('input')?.focus();
  };

  const handleCtaClick = () => {
    if (disabled || ctaBlocked || loading || ctaLoading) return;

    onCtaClick?.();

    if (!hasResolvedAddress) {
      focusAddressInput();
    }
  };

  const autocomplete = (
    <Columns alignY="center" collapseBelow="sm" space="xsmall">
      <UpliftedColumn>
        <UpliftedSearchIcon
          data-id={`${dataId}-search-icon`}
          left={{ xs: 'xlarge', sm: 'large' }}
          pointerEvents="none"
          position="absolute"
          zIndex={1}
        >
          <IconSearch color="secondaryB" isDecorative size="xsmall" variant="solid" />
        </UpliftedSearchIcon>
        <Autocomplete<AddressResult>
          id={id}
          data-id={`${dataId}-autocomplete`}
          options={options}
          muiProps={muiProps}
          textFieldProps={{
            background: 'transparent',
            domProps: {
              'aria-label': ariaLabel || label || placeholder,
              'aria-describedby': helperText ? ctaHelperTextId : undefined,
            },
            hideHelperTextSpace: true,
          }}
          error={error}
          placeholder={createSafeLabel(placeholder)}
          label={createSafeLabel(label)}
          helperText={undefined}
          disabled={disabled}
          endIcon={upliftedEndIcon}
          forcePopupIcon={forcePopupIcon}
          disableClearable
          onBlur={onBlur}
        />
      </UpliftedColumn>
      <UpliftedColumn width="content">
        <Button
          data-id={`${dataId}-cta`}
          disabled={!ctaLoading && disabled}
          status={ctaLoading ? 'loading' : undefined}
          fullWidth={{ xs: true, sm: false }}
          onClick={handleCtaClick}
          size="small"
        >
          {ctaLabel?.trim() || (hasResolvedAddress ? VIEW_PLANS_CTA_LABEL : SEARCH_CTA_LABEL)}
        </Button>
      </UpliftedColumn>
    </Columns>
  );

  return (
    <Stack data-id={dataId}>
      <Box ref={autocompleteShellRef} width="full">
        <UpliftedAutocompleteContainer
          $error={Boolean(error)}
          backgroundColor="white"
          borderRadius={{ xs: 'large', sm: 'xlarge' }}
          boxShadow="medium"
          data-id={`${dataId}-hero-shell`}
          paddingBottom={{ xs: 'medium', sm: 'none' }}
          paddingLeft={{ xs: 'medium', sm: 'none' }}
          paddingRight={{ xs: 'medium', sm: 'xsmall' }}
          paddingTop={{ xs: 'xsmall', sm: 'none' }}
          position="relative"
        >
          {autocomplete}
        </UpliftedAutocompleteContainer>
        {helperText && (
          <FormHelperText id={ctaHelperTextId} muiProps={{ error }}>
            {helperText}
          </FormHelperText>
        )}
      </Box>
      {
        <AddressManualEntryComponent
          {...manualProps}
          isOpen={showManualEntry}
          onClose={onManualEntryClose}
          optionalStreetType={optionalStreetType}
        />
      }
    </Stack>
  );
};
